import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Card, CardHeader, Checkbox, Chip, Grid, IconButton,
  InputAdornment, Menu, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import pipelineApi from '../../services/pipelineApi';
import useLockDeskStatus from '../../hooks/useLockDeskStatus';
import StatusBadge, { LOAN_STATUS_MAP } from '../../components/shared/StatusBadge';
import PageHeader from '../../components/shared/PageHeader';
import StatCard from '../../components/shared/StatCard';
import DataTable from '../../components/shared/DataTable';
import EmptyState from '../../components/shared/EmptyState';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';
import { authService } from '../../services/authService';
import { isBrokerReadOnlyRole } from '../../config/brokerAccess';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';

// Column definitions: key = camelCase property on PipelineLoanData, label = display header
const COLUMNS = [
  { key: 'loanNumber',          label: 'Loan #' },
  // lock icon column is rendered separately between col 0 and col 1
  { key: 'borrowerLastName',    label: 'Borrower' },
  { key: 'stage',               label: 'Stage' },
  { key: 'brokerCompensation',  label: 'Broker Comp' },
  { key: 'programName',         label: 'Program' },
  { key: 'underwriterDecision', label: 'UW Decision' },
  { key: 'noteRate',            label: 'Rate' },
  { key: 'loanPurpose',         label: 'Purpose' },
  { key: 'loanAmount',          label: 'Loan Amount' },
  { key: 'propertyState',       label: 'State' },
  { key: 'propertyCity',        label: 'City' },
  { key: 'ltvRatio',            label: 'LTV' },
  { key: 'loanOriginator',      label: 'Originator' },
  { key: 'lockExpirationDate',  label: 'Lock Expires' },
  { key: 'estimatedCloseDate',  label: 'Est. Close' },
  { key: 'lastUpdated',         label: 'Last Updated' },
  { key: 'intendedUsage',       label: 'Occupancy' },
  { key: 'mortgageType',        label: 'Mortgage Type' },
];

/** In Origination: no lock / no UW columns — Mortgage Type & State after Program */
const ORIGINATION_EXCLUDED_KEYS = new Set(['underwriterDecision', 'lockExpirationDate']);

function buildOriginationColumns() {
  const filtered = COLUMNS.filter((c) => !ORIGINATION_EXCLUDED_KEYS.has(c.key));
  const keys = filtered.map((c) => c.key).filter((k) => k !== 'mortgageType' && k !== 'propertyState');
  const programIdx = keys.indexOf('programName');
  const orderedKeys = [
    ...keys.slice(0, programIdx + 1),
    'mortgageType',
    'propertyState',
    ...keys.slice(programIdx + 1),
  ];
  const byKey = Object.fromEntries(COLUMNS.map((c) => [c.key, c]));
  return orderedKeys.map((k) => byKey[k]);
}

const COLUMNS_ORIGINATION = buildOriginationColumns();

// Columns hidden by default per section (user can toggle independently)
const DEFAULT_HIDDEN_ORIGINATION = { propertyCity: true, ltvRatio: true, loanOriginator: true, estimatedCloseDate: true, lastUpdated: true, intendedUsage: true, mortgageType: false, propertyState: false };
/** Submitted to UFF: Lock Expires visible by default */
const DEFAULT_HIDDEN_SUBMITTED = { propertyState: true, propertyCity: true, ltvRatio: true, loanOriginator: true, lockExpirationDate: false, estimatedCloseDate: true, lastUpdated: true, intendedUsage: true, mortgageType: true };

const UW_DECISION_CONFIG = {
  ConditionallyApprove: { label: 'Approved w/ Conditions', color: 'success', icon: CheckCircleOutlineIcon },
  ClearToClose:         { label: 'Clear to Close',        color: 'info',    icon: TaskAltIcon },
  Suspend:              { label: 'Suspended',             color: 'warning', icon: WarningAmberIcon },
  Deny:                 { label: 'Denied',                color: 'error',   icon: CancelIcon },
};

const BrokerPipelinePage = () => {
  const navigate = useNavigate();
  const brokerReadOnlyUser = isBrokerReadOnlyRole(authService.getCurrentUser()?.role);
  const { isLockDeskEnabled } = useLockDeskStatus();
  const { data: pipeline, isLoading, error } = pipelineApi.endpoints.getPipeline.useQuery();
  const [searchTerm, setSearchTerm] = useState('');
  const [columnsAnchorOrigination, setColumnsAnchorOrigination] = useState(null);
  const [columnsAnchorSubmitted, setColumnsAnchorSubmitted] = useState(null);
  const [hiddenColumnsOrigination, setHiddenColumnsOrigination] = useState(() => ({ ...DEFAULT_HIDDEN_ORIGINATION }));
  const [hiddenColumnsSubmitted, setHiddenColumnsSubmitted] = useState(() => ({ ...DEFAULT_HIDDEN_SUBMITTED }));

  // Sorting — independent per section (Submitted defaults: lock expires soonest first)
  const [sortColumnOrigination, setSortColumnOrigination] = useState(null);
  const [sortDirectionOrigination, setSortDirectionOrigination] = useState('asc');
  const [sortColumnSubmitted, setSortColumnSubmitted] = useState('lockExpirationDate');
  const [sortDirectionSubmitted, setSortDirectionSubmitted] = useState('asc');

  // Column filter state — { key: filterValue }
  const [columnFilters, setColumnFilters] = useState({});
  const [showFilterRow, setShowFilterRow] = useState(false);

  // Pagination — independent per table, 10 rows default
  const [currentPageOrigination, setCurrentPageOrigination] = useState(1);
  const [pageSizeOrigination, setPageSizeOrigination] = useState(10);
  const [currentPageSubmitted, setCurrentPageSubmitted] = useState(1);
  const [pageSizeSubmitted, setPageSizeSubmitted] = useState(10);

  const loans = pipeline?.loans ?? [];
  const summary = pipeline?.summary ?? {};

  const visibleColumnsOrigination = useMemo(
    () => COLUMNS_ORIGINATION.filter((c) => !hiddenColumnsOrigination[c.key]),
    [hiddenColumnsOrigination],
  );
  const visibleColumnsSubmitted = useMemo(
    () => COLUMNS.filter((c) => !hiddenColumnsSubmitted[c.key]),
    [hiddenColumnsSubmitted],
  );

  // Lock status helper for the dedicated lock icon column
  const getLockStatus = (loan) => {
    const exp = loan.lockExpirationDate;
    if (!exp) return 'none';
    const expDate = new Date(exp);
    if (isNaN(expDate.getTime())) return 'none';
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const expDay = new Date(expDate);
    expDay.setHours(0, 0, 0, 0);
    const daysLeft = Math.ceil((expDay - now) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return 'expired';
    if (daysLeft === 0) return 'expired';
    if (daysLeft <= 7) return 'expiring';
    return 'locked';
  };

  const goPrice = useCallback((loan) => (e) => {
    e.stopPropagation();
    navigate(`/broker/loans/${loan.loanId}`, { state: { postImportAction: 'price' } });
  }, [navigate]);

  const lockIcon = (loan) => {
    const status = getLockStatus(loan);
    const goView = (e) => { e.stopPropagation(); navigate(`/broker/loans/${loan.loanId}`); };
    const goPostLock = (e) => { e.stopPropagation(); navigate(`/broker/loans/${loan.loanId}`, { state: { postImportAction: 'postlock' } }); };
    if (brokerReadOnlyUser) {
      switch (status) {
        case 'expired':
          return <Tooltip title="Lock expired — view loan"><IconButton size="small" onClick={goView}><LockIcon fontSize="small" sx={{ color: 'error.main' }} /></IconButton></Tooltip>;
        case 'expiring':
          return <Tooltip title="Lock expiring — view loan"><IconButton size="small" onClick={goView}><LockIcon fontSize="small" sx={{ color: 'warning.main' }} /></IconButton></Tooltip>;
        case 'locked':
          return <Tooltip title="Locked — view loan"><IconButton size="small" onClick={goView}><LockIcon fontSize="small" sx={{ color: 'success.main' }} /></IconButton></Tooltip>;
        default:
          return <Tooltip title="Not locked — view loan"><IconButton size="small" onClick={goView}><LockOpenIcon fontSize="small" sx={{ color: 'text.disabled' }} /></IconButton></Tooltip>;
      }
    }
    switch (status) {
      case 'expired':
        return <Tooltip title="Lock expired — click for post-lock actions"><IconButton size="small" onClick={goPostLock}><LockIcon fontSize="small" sx={{ color: 'error.main' }} /></IconButton></Tooltip>;
      case 'expiring':
        return <Tooltip title="Lock expiring soon — click for post-lock actions"><IconButton size="small" onClick={goPostLock}><LockIcon fontSize="small" sx={{ color: 'warning.main' }} /></IconButton></Tooltip>;
      case 'locked':
        return <Tooltip title="Locked — click for post-lock actions"><IconButton size="small" onClick={goPostLock}><LockIcon fontSize="small" sx={{ color: 'success.main' }} /></IconButton></Tooltip>;
      default:
        return (
          <Tooltip title={isLockDeskEnabled ? 'Not locked — click to run pricing' : 'Not locked — Lock Desk is currently closed; pricing is available but locking is paused'}>
            <IconButton size="small" onClick={goPrice(loan)}><LockOpenIcon fontSize="small" sx={{ color: isLockDeskEnabled ? 'text.disabled' : 'warning.main' }} /></IconButton>
          </Tooltip>
        );
    }
  };

  const toggleColumnOrigination = (key) => {
    setHiddenColumnsOrigination((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const toggleColumnSubmitted = (key) => {
    setHiddenColumnsSubmitted((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Detect low-cardinality columns for dropdown filters
  const columnUniqueValues = useMemo(() => {
    const map = {};
    const MAX_DROPDOWN = 20;
    const dropdownKeys = ['stage', 'loanPurpose', 'propertyState', 'loanOriginator', 'intendedUsage', 'mortgageType', 'underwriterDecision'];
    dropdownKeys.forEach(key => {
      const vals = new Set();
      loans.forEach(loan => {
        const v = loan[key];
        if (v != null && String(v).trim() !== '') vals.add(String(v));
      });
      if (vals.size > 0 && vals.size <= MAX_DROPDOWN) map[key] = [...vals].sort();
    });
    return map;
  }, [loans]);

  // Sort helper
  const getSortValue = useCallback((key, loan) => {
    const raw = loan[key];
    if (raw == null || raw === '') return null;
    if (typeof raw === 'number') return raw;
    const s = String(raw);
    // ISO dates
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) { const d = new Date(s); if (!isNaN(d.getTime())) return d.getTime(); }
    // Numeric strings
    const num = parseFloat(s.replace(/[^0-9.-]/g, ''));
    if (!isNaN(num)) return num;
    return s.toLowerCase();
  }, []);

  const sortLoanList = useCallback((list, sortColumn, sortDirection) => {
    if (!sortColumn) return list;
    return [...list].sort((a, b) => {
      const aVal = getSortValue(sortColumn, a);
      const bVal = getSortValue(sortColumn, b);
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [getSortValue]);

  // Pipeline: search + column filters only (no sort — applied per section)
  const filteredLoans = useMemo(() => {
    let result = [...loans];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((loan) =>
        COLUMNS.some((col) => {
          const v = loan[col.key];
          return v != null && String(v).toLowerCase().includes(term);
        }),
      );
    }

    Object.entries(columnFilters).forEach(([key, filterVal]) => {
      if (!filterVal) return;
      result = result.filter((loan) => {
        const cellVal = String(loan[key] ?? '');
        if (columnUniqueValues[key]) return cellVal === filterVal;
        return cellVal.toLowerCase().includes(filterVal.toLowerCase());
      });
    });

    return result;
  }, [loans, searchTerm, columnFilters, columnUniqueValues]);

  const originationLoansSorted = useMemo(
    () => sortLoanList(
      filteredLoans.filter((l) => (l.stage || '').toLowerCase() === 'origination'),
      sortColumnOrigination,
      sortDirectionOrigination,
    ),
    [filteredLoans, sortLoanList, sortColumnOrigination, sortDirectionOrigination],
  );

  const submittedLoansSorted = useMemo(
    () => sortLoanList(
      filteredLoans.filter((l) => (l.stage || '').toLowerCase() !== 'origination'),
      sortColumnSubmitted,
      sortDirectionSubmitted,
    ),
    [filteredLoans, sortLoanList, sortColumnSubmitted, sortDirectionSubmitted],
  );

  const totalFiltered = filteredLoans.length;
  const totalOrigination = originationLoansSorted.length;
  const totalSubmitted = submittedLoansSorted.length;

  const totalPagesOrigination = Math.max(1, Math.ceil(totalOrigination / pageSizeOrigination) || 1);
  const safeCurrentPageOrigination = Math.min(currentPageOrigination, totalPagesOrigination);
  const paginatedOrigination = originationLoansSorted.slice(
    (safeCurrentPageOrigination - 1) * pageSizeOrigination,
    safeCurrentPageOrigination * pageSizeOrigination,
  );

  const totalPagesSubmitted = Math.max(1, Math.ceil(totalSubmitted / pageSizeSubmitted) || 1);
  const safeCurrentPageSubmitted = Math.min(currentPageSubmitted, totalPagesSubmitted);
  const paginatedSubmitted = submittedLoansSorted.slice(
    (safeCurrentPageSubmitted - 1) * pageSizeSubmitted,
    safeCurrentPageSubmitted * pageSizeSubmitted,
  );

  const handleSortOrigination = (key) => {
    if (sortColumnOrigination === key) {
      setSortDirectionOrigination((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumnOrigination(key);
      setSortDirectionOrigination('asc');
    }
    setCurrentPageOrigination(1);
  };

  const handleSortSubmitted = (key) => {
    if (sortColumnSubmitted === key) {
      setSortDirectionSubmitted((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumnSubmitted(key);
      setSortDirectionSubmitted('asc');
    }
    setCurrentPageSubmitted(1);
  };

  const handleColumnFilter = (key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPageOrigination(1);
    setCurrentPageSubmitted(1);
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setSearchTerm('');
    setCurrentPageOrigination(1);
    setCurrentPageSubmitted(1);
  };

  const hasActiveFilters = searchTerm || Object.values(columnFilters).some(v => v);

  // --- Formatting helpers ---
  const fmtCurrency = (v) => {
    if (v == null) return '—';
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^0-9.-]/g, ''));
    return isNaN(n) ? '—' : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const fmtRate = (v) => {
    if (v == null) return '—';
    return `${Number(v).toFixed(3)}%`;
  };

  const fmtRatio = (v) => {
    if (v == null) return '—';
    return `${Number(v).toFixed(2)}%`;
  };

  const fmtDate = (v) => {
    if (v == null) return '—';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const stageBadge = (v) => {
    if (!v) return '—';
    return <StatusBadge status={v} map={LOAN_STATUS_MAP}>{v}</StatusBadge>;
  };

  const lockExpDisplay = (v) => {
    if (!v) return '—';
    const exp = new Date(v);
    if (isNaN(exp.getTime())) return '—';
    const daysLeft = Math.ceil((exp - new Date()) / (1000 * 60 * 60 * 24));
    const formatted = fmtDate(v);
    if (daysLeft < 0) return <Typography variant="body2" component="span" color="error">{formatted} <Chip label="Expired" size="small" color="error" variant="outlined" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} /></Typography>;
    if (daysLeft <= 7) return <Typography variant="body2" component="span" color="warning.main" fontWeight={700}>{formatted} <Chip label={`${daysLeft}d`} size="small" color="warning" variant="outlined" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} /></Typography>;
    return formatted;
  };

  // Render a cell value based on column key
  const renderCell = (key, loan) => {
    const v = loan[key];
    switch (key) {
      case 'loanAmount':
        return fmtCurrency(v);
      case 'brokerCompensation': {
        const compType = loan.compensationPaidByType;
        const typeBadge = compType
          ? compType.toLowerCase().includes('lender')
            ? <Chip label="LPC" size="small" color="success" variant="contained" sx={{ ml: 0.5, height: 18, fontSize: '0.6rem' }} />
            : <Chip label="BPC" size="small" color="info" variant="contained" sx={{ ml: 0.5, height: 18, fontSize: '0.6rem' }} />
          : null;
        return (
          <Typography variant="body2" component="span" color="success.main" fontWeight={700}>
            {fmtCurrency(v)}{typeBadge}
          </Typography>
        );
      }
      case 'noteRate':
        return fmtRate(v);
      case 'ltvRatio':
        return fmtRatio(v);
      case 'stage':
        return stageBadge(v);
      case 'underwriterDecision': {
        if (!v) return <Typography variant="body2" color="text.secondary">—</Typography>;
        const cfg = UW_DECISION_CONFIG[v] || { label: v, color: 'default', icon: HelpOutlineIcon };
        const Icon = cfg.icon;
        return <Chip icon={<Icon sx={{ fontSize: 14 }} />} label={cfg.label} size="small" color={cfg.color} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />;
      }
      case 'lockExpirationDate':
        return lockExpDisplay(v);
      case 'lockConfirmedDate':
      case 'lockRequestedDate':
      case 'applicationDate':
      case 'submittedToUnderwritingDate':
      case 'estimatedCloseDate':
      case 'lastUpdated':
        return fmtDate(v);
      default:
        return v != null && String(v).trim() !== '' ? String(v) : '—';
    }
  };

  const renderFilterCell = (col) => {
    if (columnUniqueValues[col.key]) {
      return (
        <TextField select size="small" value={columnFilters[col.key] || ''} onChange={(e) => handleColumnFilter(col.key, e.target.value)} sx={{ minWidth: 80, '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 } }}>
          <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
          {columnUniqueValues[col.key].map(v => (
            <MenuItem key={v} value={v} sx={{ fontSize: '0.75rem' }}>{col.key === 'underwriterDecision' ? (UW_DECISION_CONFIG[v]?.label || v) : v}</MenuItem>
          ))}
        </TextField>
      );
    }
    return (
      <TextField size="small" placeholder="Filter..." value={columnFilters[col.key] || ''} onChange={(e) => handleColumnFilter(col.key, e.target.value)} sx={{ minWidth: 60, '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 } }} />
    );
  };

  /** Build DataTable column defs — first column, dedicated lock column, then the rest. */
  const buildTableColumns = (visibleColumns) => {
    const toDef = (col) => ({
      key: col.key,
      label: col.label,
      renderCell: (loan) => renderCell(col.key, loan),
      renderFilter: () => renderFilterCell(col),
    });
    return [
      ...visibleColumns.slice(0, 1).map(toDef),
      {
        key: '_lock',
        sortable: false,
        width: '1%',
        align: 'center',
        headerContent: <LockIcon sx={{ fontSize: 16, color: 'text.secondary', verticalAlign: 'middle' }} />,
        renderCell: (loan) => lockIcon(loan),
      },
      ...visibleColumns.slice(1).map(toDef),
    ];
  };

  if (isLoading) {
    return <LoadingSkeleton variant="page" cards={6} />;
  }

  if (error) {
    return (
      <EmptyState
        icon={<ErrorOutlineIcon />}
        title="Failed to load pipeline"
        description={getAxiosErrorMessage(error, 'An error occurred while fetching your pipeline.')}
      />
    );
  }

  return (
    <Box>
      <PageHeader title="My Pipeline" subtitle="Your active loan submissions" />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
          <StatCard label="Active Loans" value={loans.length} caption="in pipeline" icon={<ListAltIcon />} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
          <StatCard
            label="Total Volume"
            value={`$${((summary.totalVolume || 0) / 1000000).toFixed(2)}M`}
            caption={`Avg $${((summary.avgLoanAmount || 0) / 1000).toFixed(0)}K per loan`}
            icon={<AccountBalanceOutlinedIcon />}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
          <StatCard
            label="Est. Compensation"
            value={`$${(summary.totalCompensation || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            caption={`Avg $${(() => { const withComp = loans.filter(l => l.brokerCompensation && l.brokerCompensation > 0); return withComp.length > 0 ? Math.round((summary.totalCompensation || 0) / withComp.length).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 0; })()} per loan`}
            color="success.main"
            icon={<PaidOutlinedIcon />}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
          <StatCard
            label="Locks Expiring"
            value={summary.locksExpiringSoon || 0}
            caption="within 7 days"
            color={(summary.locksExpiringSoon || 0) > 0 ? 'warning.main' : undefined}
            icon={<TimerOutlinedIcon />}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
          <StatCard
            label="Approved w/ Conditions"
            value={summary.uwApprovedWithConditions || 0}
            caption="conditionally approved"
            color={(summary.uwApprovedWithConditions || 0) > 0 ? 'success.main' : undefined}
            icon={<CheckCircleOutlineIcon />}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
          <StatCard
            label="Clear to Close"
            value={summary.uwClearToClose || 0}
            caption="ready for closing"
            color={(summary.uwClearToClose || 0) > 0 ? 'info.main' : undefined}
            icon={<TaskAltIcon />}
          />
        </Grid>
      </Grid>

      {/* ── Toolbar: Search, Filters, Columns ── */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={1} sx={{ px: 2, py: 1 }}>
          <TextField
            size="small" placeholder="Search loans..." value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPageOrigination(1);
              setCurrentPageSubmitted(1);
            }}
            sx={{ width: 250 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: searchTerm ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearchTerm('')}><ClearIcon fontSize="small" /></IconButton></InputAdornment> : undefined,
            }}
          />
          <Button
            size="small" variant={showFilterRow ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />} onClick={() => setShowFilterRow(!showFilterRow)}
          >Filters</Button>
          {hasActiveFilters && (
            <Button size="small" variant="outlined" color="error" startIcon={<ClearIcon />} onClick={clearAllFilters}>Clear</Button>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>{totalFiltered} of {loans.length} loans shown</Typography>
        </Stack>
      </Card>

      {/* ── Section 1: In Your Court (Origination) ── */}
      <Card variant="outlined" sx={{ mb: 2, borderLeft: '3px solid', borderLeftColor: 'info.main' }}>
        <CardHeader
          avatar={<EditNoteIcon color="info" />}
          title={<Stack direction="row" alignItems="center" spacing={1}><Typography variant="subtitle1" fontWeight={700}>In Origination</Typography><Chip label={totalOrigination} size="small" color="info" /></Stack>}
          action={(
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" justifyContent="flex-end">
              <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>Loans not yet submitted to UFF</Typography>
              <Button size="small" variant="outlined" startIcon={<ViewColumnIcon />} onClick={(e) => setColumnsAnchorOrigination(e.currentTarget)}>Columns</Button>
              <Menu anchorEl={columnsAnchorOrigination} open={Boolean(columnsAnchorOrigination)} onClose={() => setColumnsAnchorOrigination(null)} slotProps={{ paper: { sx: { maxHeight: 400 } } }}>
                {COLUMNS_ORIGINATION.map((col) => (
                  <MenuItem key={col.key} dense onClick={() => toggleColumnOrigination(col.key)}>
                    <Checkbox size="small" checked={!hiddenColumnsOrigination[col.key]} sx={{ p: 0, mr: 1 }} />
                    <Typography variant="body2">{col.label}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Stack>
          )}
          sx={{ py: 1 }}
        />
        <DataTable
          columns={buildTableColumns(visibleColumnsOrigination)}
          rows={paginatedOrigination}
          rowKey={(loan) => loan.loanId}
          onRowClick={(loan) => navigate(`/broker/loans/${loan.loanId}`)}
          sortColumn={sortColumnOrigination}
          sortDirection={sortDirectionOrigination}
          onSort={handleSortOrigination}
          showFilterRow={showFilterRow}
          emptyProps={{
            icon: <CheckCircleOutlineIcon />,
            title: 'No loans in origination',
            description: hasActiveFilters ? 'No origination loans match your filters.' : 'All caught up!',
          }}
          pagination={{
            page: safeCurrentPageOrigination,
            pageSize: pageSizeOrigination,
            total: totalOrigination,
            onPageChange: setCurrentPageOrigination,
            onPageSizeChange: (size) => { setPageSizeOrigination(size); setCurrentPageOrigination(1); },
            itemLabel: 'loans',
          }}
        />
      </Card>

      {/* ── Section 2: Submitted to UFF ── */}
      <Card variant="outlined" sx={{ borderLeft: '3px solid', borderLeftColor: 'primary.main' }}>
        <CardHeader
          avatar={<SendIcon color="primary" />}
          title={<Stack direction="row" alignItems="center" spacing={1}><Typography variant="subtitle1" fontWeight={700}>Submitted to UFF</Typography><Chip label={totalSubmitted} size="small" color="primary" /></Stack>}
          action={(
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" justifyContent="flex-end">
              <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>Loans in UFF&apos;s pipeline</Typography>
              <Button size="small" variant="outlined" startIcon={<ViewColumnIcon />} onClick={(e) => setColumnsAnchorSubmitted(e.currentTarget)}>Columns</Button>
              <Menu anchorEl={columnsAnchorSubmitted} open={Boolean(columnsAnchorSubmitted)} onClose={() => setColumnsAnchorSubmitted(null)} slotProps={{ paper: { sx: { maxHeight: 400 } } }}>
                {COLUMNS.map((col) => (
                  <MenuItem key={col.key} dense onClick={() => toggleColumnSubmitted(col.key)}>
                    <Checkbox size="small" checked={!hiddenColumnsSubmitted[col.key]} sx={{ p: 0, mr: 1 }} />
                    <Typography variant="body2">{col.label}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Stack>
          )}
          sx={{ py: 1 }}
        />
        <DataTable
          columns={buildTableColumns(visibleColumnsSubmitted)}
          rows={paginatedSubmitted}
          rowKey={(loan) => loan.loanId}
          onRowClick={(loan) => navigate(`/broker/loans/${loan.loanId}`)}
          sortColumn={sortColumnSubmitted}
          sortDirection={sortDirectionSubmitted}
          onSort={handleSortSubmitted}
          showFilterRow={showFilterRow}
          emptyProps={{
            title: 'No submitted loans',
            description: hasActiveFilters ? 'No submitted loans match your filters.' : 'Loans appear here once submitted to UFF.',
          }}
          pagination={{
            page: safeCurrentPageSubmitted,
            pageSize: pageSizeSubmitted,
            total: totalSubmitted,
            onPageChange: setCurrentPageSubmitted,
            onPageSizeChange: (size) => { setPageSizeSubmitted(size); setCurrentPageSubmitted(1); },
            itemLabel: 'submitted loans',
          }}
        />
      </Card>
    </Box>
  );
};

export default BrokerPipelinePage;
